const Logger = require("../Logger");
const FloatingNotificationsController = require("../UI/FloatingNotificationsController");

const INVALID_DISPLAY_CONTROL_CONFIGURATION = "E000000107";
const DISPLAY_CONTROL_COMMAND_EXECUTION_ERROR = "E000000108";
const SERIAL_PORT_READ_ERROR = "E000000114";
const UNEXPECTED_ERROR = "E999999999";

const SERIAL_BAUD_RATE_KEY = "serial-baud-rate";
const SERIAL_DATA_BITS_KEY = "serial-data-bits";
const SERIAL_STOP_BITS_KEY = "serial-stop-bits";
const SERIAL_PARITY_KEY = "serial-parity";
const SERIAL_FLOW_CONTROL_KEY = "serial-flow-control";
const SCREEN_ON_COMMAND_KEY = "serial-screen-on-cmd"
const SCREEN_OFF_COMMAND_KEY = "serial-screen-off-cmd"

const COMMAND_REGEX = /^([0-9a-f]{2})(\s*[0-9a-f]{2})*$/

// According to https://developer.mozilla.org/en-US/docs/Web/API/SerialPort/open#parity
const SUPPORTED_PARITY_VALUES = [ "none", "odd", "even" ];

// Defaults as stated in https://docs.google.com/document/d/10nASFHw_LKRdUwO8rTMBhr1SsXU8wwT1QymAjOnWcuA/edit?tab=t.0#heading=h.os8cukd6g3u0
const DEFAULT_WEB_SERIAL_API_OPTIONS = {
  baudRate: 9600,
  dataBits: 8,
  stopBits: 1,
  parity: "none",
  flowControl: "none"
};

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const MANUAL_CONFIGURATION_TIMEOUT = 2 * MINUTES;
const DEFAULT_READ_LOOP_DELAY = 1 * SECONDS;

const service = {};

service.configured = false;
service.webSerialApiOptions = { ...DEFAULT_WEB_SERIAL_API_OPTIONS };
service.powerOnCommand = null;
service.powerOffCommand = null;
service.selectedPort = null;
service.serialConnectionReady = false;
service.serialPortReaderConfigured = false;
service.pendingCommand = null;
service.readLoopDelay = DEFAULT_READ_LOOP_DELAY;
service.serialConnectionReadyListeners = [];

service.isConfigured = () => service.configured;

service.powerCommandsAvailable = () => service.powerOnCommand !== null && service.powerOffCommand !== null;

service.isSerialConnectionReady = () => service.serialConnectionReady;

service.isSerialPortReaderConfigured = () => service.serialPortReaderConfigured;

const extractBaudRate = ( options, baudRateText ) => {
  if( !baudRateText || typeof baudRateText !== "string" ) {
    return;
  }

  const baudRate = Number( baudRateText );

  if( !Number.isInteger( baudRate ) || baudRate <= 0 ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Unsupported baud rate value; defaulting to 9600",
      baudRateText
    );

    return;
  }

  options.baudRate = baudRate;
}

const extractDataBits = ( options, dataBitsText ) => {
  if( !dataBitsText || typeof dataBitsText !== "string" ) {
    return;
  }

  const dataBits = Number( dataBitsText );

  if( !Number.isInteger( dataBits ) || dataBits < 5 || dataBits > 8 ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Unsupported data bits value; defaulting to 8",
      dataBitsText
    );

    return;
  }

  options.dataBits = dataBits;
}

const extractStopBits = ( options, stopBitsText ) => {
  if( !stopBitsText || typeof stopBitsText !== "string" ) {
    return;
  }

  const stopBits = Number( stopBitsText );

  if( stopBits !== 1 && stopBits !== 2 ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Unsupported stop bits value; defaulting to 1",
      stopBitsText
    );

    return;
  }

  options.stopBits = stopBits;
}

const extractParity = ( options, parityText ) => {
  if( !parityText || typeof parityText !== "string" ) {
    return;
  }

  const parity = parityText.toLowerCase();

  if( !SUPPORTED_PARITY_VALUES.includes( parity ) ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Unsupported serial parity value; defaulting to none",
      parityText
    );

    return;
  }

  options.parity = parity;
}

const extractFlowControl = ( options, flowControlText ) => {

  if( !flowControlText || typeof flowControlText !== "string" ) {
    return;
  }

  const flowControl = flowControlText.toLowerCase().replace( "/", "" );

  // See https://developer.mozilla.org/en-US/docs/Web/API/SerialPort/open#flowcontrol
  if( flowControl === "rtscts" || flowControl === "hardware" ) {
    options.flowControl = "hardware";
  } else if( flowControl !== "none" ) {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Unsupported serial flow control value; defaulting to none",
      flowControl
    );
  }
}

service.parseCommand = ( key, input ) => {
  if( !input || typeof input !== "string" ) {
    throw Error( `RS232 - Required command not provided '${ key }'` );
  }

  const command = input.toLowerCase().trim();

  if( !COMMAND_REGEX.test(command) ) {
    throw Error( `RS232 - Invalid '${ key }' command format: '${ input }'` );
  }

  const sanitized = command.replace(/\s+/g, "");
  const bytes = sanitized.match(/.{2}/g).map( h => parseInt( h, 16 ) );

  return new Uint8Array( bytes );
}

service.extractWebSerialApiOptions = settings => {
  const options = { ...DEFAULT_WEB_SERIAL_API_OPTIONS };

  extractBaudRate( options, settings[SERIAL_BAUD_RATE_KEY] );
  extractDataBits( options, settings[SERIAL_DATA_BITS_KEY] );
  extractStopBits( options, settings[SERIAL_STOP_BITS_KEY] );
  extractParity( options, settings[SERIAL_PARITY_KEY] );
  extractFlowControl( options, settings[SERIAL_FLOW_CONTROL_KEY] );

  return options;
};

const getPortInfo = port => {
  try {
    return port.getInfo && JSON.stringify( port.getInfo() );
  } catch( error ) {
    return "";
  }
}

const checkWebSerialApiOptionsValid = ( options, serialPortCommunicationHandler ) => {
  if( !serialPortCommunicationHandler ) {
    throw Error( "Bug: serial port communication handler not provided" );
  }

  const settingsMatch = serialPortCommunicationHandler.areWebSerialApiOptionsValid( options );

  if( !settingsMatch ) {
    const displayType = serialPortCommunicationHandler.getType();

    throw Error( `Display control serial connection settings do not match ${ displayType } connection settings:
      '${ JSON.stringify( options ) }'`
    );
  }
}

const checkPowerCommandsValid = ( powerOnCommand, powerOffCommand, serialPortCommunicationHandler ) => {
  if( !serialPortCommunicationHandler ) {
    throw Error( "Bug: serial port communication handler not provided" );
  }

  const settingsMatch = serialPortCommunicationHandler.arePowerCommandsValid( powerOnCommand, powerOffCommand );

  if( !settingsMatch ) {
    const displayType = serialPortCommunicationHandler.getType();

    throw Error( `Display control power commands do not match ${ displayType } connection settings:
      '${ JSON.stringify( powerOnCommand ) }',
      '${ JSON.stringify( powerOffCommand ) }'`
    );
  }
}

service.checkConnectionSettingsMatchHandlerExpectations = serialPortCommunicationHandler => {
  checkWebSerialApiOptionsValid( service.webSerialApiOptions, serialPortCommunicationHandler );

  if( !service.powerOnCommand || !service.powerOffCommand ) {
    // Commands still not configured; so settings are compatible so far with handler connection settings.
    return;
  }

  checkPowerCommandsValid( service.powerOnCommand, service.powerOffCommand, serialPortCommunicationHandler );
}

service.setupPort = port => {
  service.selectedPort = port;

  return port.open( service.webSerialApiOptions )
  .then( () => {
    const portInfo = getPortInfo( port );

    console.log( "Serial port opened", service.webSerialApiOptions, portInfo );
    Logger.viewerInfo(
      "Serial port opened",
      { settings: JSON.stringify( service.webSerialApiOptions ), port: portInfo }
    );

    if( !port.writable ) {
      Logger.viewerError(
        INVALID_DISPLAY_CONTROL_CONFIGURATION,
        "Serial port is not writable",
        { settings: JSON.stringify( service.webSerialApiOptions ), port: portInfo }
      );

      service.serialConnectionReady = false;
      return false;
    }

    service.serialConnectionReady = true;

    // Send pending command now that the serial port is open
    if( service.pendingCommand ) {
      const command = service.pendingCommand;
      service.pendingCommand = null;

      return service.sendCommand( command ).then( () => true );
    }

    return true;
  })
  .then(ready => {
    ready && service.notifySerialConnectionReady();

    return ready;
  })
  .catch( error => {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Could not open serial port",
      {
        settings: JSON.stringify( service.webSerialApiOptions ),
        port: getPortInfo( port ),
        error: error && ( error.stack || error.message )
      }
    );

    service.serialConnectionReady = false;
    return false;
  });
}

service.tryConfigureConnection = () => {
  return navigator.serial.getPorts()
  .then( ports => {
    if( ports.length === 0 ) {
      console.warn( 'No serial ports found, manual configuration required' );
      showSerialPortConfigurationNotification();

      return Promise.resolve();
    }

    return service.setupPort( ports[0] );
  });
}

const validateConfigurationPrerequisites = () => {
  return Promise.resolve().then(() => {
    if(
      !navigator.serial ||
      typeof navigator.serial.getPorts !== "function" ||
      typeof navigator.serial.requestPort !== "function"
    ) {
      throw Error( 'WebSerial API is not supported by this browser' );
    }
  });
}

service.configure = settings => {
  return validateConfigurationPrerequisites().then(() => {
    const options = service.extractWebSerialApiOptions(settings);
    const powerOnCommand = service.parseCommand( SCREEN_ON_COMMAND_KEY, settings[SCREEN_ON_COMMAND_KEY] );
    const powerOffCommand = service.parseCommand( SCREEN_OFF_COMMAND_KEY, settings[SCREEN_OFF_COMMAND_KEY] );

    service.configured = true;
    service.webSerialApiOptions = options;
    service.powerOnCommand = powerOnCommand;
    service.powerOffCommand = powerOffCommand;

    console.warn( "WebSerialDisplayControl.configure", JSON.stringify(service.webSerialApiOptions), powerOnCommand, powerOffCommand );

    return service.tryConfigureConnection();
  });
};

service.configureSpecificDeviceConnection = serialPortCommunicationHandler => {
  return validateConfigurationPrerequisites().then(() => {
    const options = serialPortCommunicationHandler.getDeviceSpecificWebSerialApiOptions();

    service.configured = true;
    service.webSerialApiOptions = options;
    // Display control has not been configured yet; so power commands are not available
    service.powerOnCommand = null;
    service.powerOffCommand = null;

    console.warn( "WebSerialDisplayControl.configureSpecificDeviceConnection", JSON.stringify(service.webSerialApiOptions) );

    return service.tryConfigureConnection();
  });
}

service.configurePowerCommands = ( settings, serialPortCommunicationHandler ) => {
  const options = service.extractWebSerialApiOptions(settings);

  checkWebSerialApiOptionsValid( options, serialPortCommunicationHandler );

  const powerOnCommand = service.parseCommand( SCREEN_ON_COMMAND_KEY, settings[SCREEN_ON_COMMAND_KEY] );
  const powerOffCommand = service.parseCommand( SCREEN_OFF_COMMAND_KEY, settings[SCREEN_OFF_COMMAND_KEY] );

  checkPowerCommandsValid( powerOnCommand, powerOffCommand, serialPortCommunicationHandler );

  service.powerOnCommand = powerOnCommand;
  service.powerOffCommand = powerOffCommand;
}

service.resetPowerCommands = () => {
  service.powerOnCommand = null;
  service.powerOffCommand = null;
}

const readNextSerialPortData = ( reader, serialPortCommunicationHandler ) => {
  const closeReader = () => {
    console.warn( "Serial port reader loop ended" );
  
    reader.releaseLock();
  }

  return Promise.resolve()
  .then(() => reader.read())
  .then( ({ value, done }) => {
    if( done ) {
      closeReader();
      return;
    }

    const bytes = Array.from( value );
    try {
      serialPortCommunicationHandler.handleSerialPortData( bytes );
    } catch( error ) {
      Logger.viewerError(
        UNEXPECTED_ERROR,
        "Serial port data handler error",
        { error: error && ( error.stack || error.message ) }
      );
    }
 
    setTimeout(() => {
      readNextSerialPortData( reader, serialPortCommunicationHandler );
    }, service.readLoopDelay); // Small delay to minimize CPU usage if loop is too fast
  }).catch( error => {
    Logger.viewerError(
      SERIAL_PORT_READ_ERROR,
      "Serial port read error",
      { error: error && ( error.stack || error.message ) }
    );

    closeReader();
  });
}

service.configureSerialPortReader = serialPortCommunicationHandler => {
  return Promise.resolve().then(() => {
    if( service.serialPortReaderConfigured ) {
      console.log( "Serial port reader already configured, returning" );
  
      return false;
    }

    if( !service.selectedPort.readable ) {
      throw Error( "Serial port is not readable" );
    }

    const reader = service.selectedPort.readable.getReader();

    // Read the serial port data in a loop in a different thread ( to prevent blocking the main thread )
    // That's why we don't wait for the promise to resolve here.
    readNextSerialPortData( reader, serialPortCommunicationHandler );

    service.serialPortReaderConfigured = true;
    return true;
  });
}

service.stop = () => {
  if( service.selectedPort ) {
    try {
      if( typeof service.selectedPort.close === "function" ) {
        service.selectedPort.close();
      }
    } catch( error ) {
      console.error( "Error closing serial port", error );
    }
  }

  service.webSerialApiOptions = { ...DEFAULT_WEB_SERIAL_API_OPTIONS };
  service.configured = false;
  service.powerOnCommand = null;
  service.powerOffCommand = null;
  service.selectedPort = null;
  service.serialConnectionReady = false;
  service.serialPortReaderConfigured = false;
  service.pendingCommand = null;
  service.readLoopDelay = DEFAULT_READ_LOOP_DELAY;
  service.serialConnectionReadyListeners = [];
};

service.handleSerialPortConfigurationButtonClick = skip => {
  console.log( 'Serial port configuration button clicked' );

  return navigator.serial.requestPort()
  .then( service.setupPort )
  .then( success => success && skip() )
  .catch( error => {
    Logger.viewerError(
      INVALID_DISPLAY_CONTROL_CONFIGURATION,
      "Could not select a valid serial port",
      { error: error && ( error.stack || error.message ) }
    );

    skip();
  });
}

const showSerialPortConfigurationNotification = () => {
  FloatingNotificationsController.addNotification(
    getNotificationMarkup(),
    MANUAL_CONFIGURATION_TIMEOUT,
    skip => {
      document.getElementById("serial-port-configuration-button")
      .addEventListener( 'click', () => service.handleSerialPortConfigurationButtonClick(skip) )
    }
  );

  FloatingNotificationsController.showNotifications();
}

const getNotificationMarkup = () => {
  return `
    <p>Serial port configuration requires manual intervention.</p>
    <p>
      Once the proper cable has been attached to RS-232 or USB port and to the TV/monitor, 
      please click the button below to configure serial port communication.
    </p>
    <p>
      <button id="serial-port-configuration-button" type="button">Configure</button>
    </p>
  `;
}

service.sendCommand = ( command, options = {} ) => {
  return Promise.resolve().then(() => {
    const writer = service.selectedPort.writable.getWriter();

    return writer.write( command )
    .then(() => {
      console.log( `Sent serial port command: '${ command.toString() }'` );

      return true;
    })
    .finally(() => {
      try {
        writer.releaseLock();
      } catch( error ) {
        Logger.viewerError(
          UNEXPECTED_ERROR,
          "Error releasing serial port writer lock",
          { error: error && ( error.stack || error.message ) }
        );
      }
    });
  })
  .catch( error => {
    const errorCode = ( options && options.errorCode ) || DISPLAY_CONTROL_COMMAND_EXECUTION_ERROR;

    Logger.viewerError(
      errorCode,
      "Could not send serial port command",
      { command: command.toString(), error: error && ( error.stack || error.message ) }
    );

    return false;
  });
};

service.turnDisplayOn = () => {
  if( !service.serialConnectionReady || !service.powerOnCommand ) {
    service.pendingCommand = service.powerOnCommand;

    return Promise.resolve();
  }

  return service.sendCommand( service.powerOnCommand );
};

service.turnDisplayOff = () => {
  if( !service.serialConnectionReady || !service.powerOffCommand ) {
    service.pendingCommand = service.powerOffCommand;

    return Promise.resolve();
  }

  return service.sendCommand( service.powerOffCommand );
};

service.addSerialConnectionReadyListener = listener => {
  if( !listener || typeof listener.onSerialConnectionReady !== 'function' ) {
    throw new Error( 'Listener must be an object with onSerialConnectionReady method' );
  }

  service.serialConnectionReadyListeners.push( listener );
}

service.removeSerialConnectionReadyListener = listener => {
  service.serialConnectionReadyListeners = service.serialConnectionReadyListeners.filter( l => l !== listener );
}

service.notifySerialConnectionReady = () => {
  service.serialConnectionReadyListeners.forEach( listener => {
    try {
      listener.onSerialConnectionReady();
    } catch( error ) {
      Logger.viewerError(
        UNEXPECTED_ERROR,
        "Error notifying serial connection ready listener",
        { error: error && ( error.stack || error.message ) }
      );
    }
  });
}

service.forgetAllPorts = () => {
  if( !navigator.serial || typeof navigator.serial.getPorts !== "function" ) {
    return Promise.resolve();
  }

  return navigator.serial.getPorts().then( ports => {
    if( ports.length ) {
      Logger.viewerImportant(
        "Forgetting ports, user will need to reconfigure manually if features are re-enabled",
        { count: ports.length }
      );
    }

    ports.forEach( port => {
      port.forget().catch( error => {
        // No need to send to BQ
        console.error( "Error forgetting port", error );
      });
    });
  }).catch( error => {
    Logger.viewerError(
      UNEXPECTED_ERROR,
      "Error forgetting ports",
      { error: error && ( error.stack || error.message ) }
    );
  });
}

module.exports = service;
